import React, { useState } from "react";
import { motion } from "framer-motion"; 
import { ExternalLink, Layers, Globe } from "lucide-react"; 

// --- Individual Project Card ---
function ContentBlock({ title, count, reverse, url, desc, tags }) {
  const direction = reverse ? "lg:flex-row-reverse" : "lg:flex-row";

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="flex w-full items-start gap-4 group"
    >
      {/* Side Numbering */}
      <div className="hidden md:block text-5xl font-black text-white/5 group-hover:text-[#00F5A0]/20 transition-colors duration-500 mt-2">
        {count}
      </div>

      <div
        className="flex-1 p-5 rounded-3xl relative overflow-hidden transition-all duration-500 hover:shadow-[0_0_30px_rgba(0,245,160,0.15)]"
        style={{
          background: "linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className={`flex flex-col ${direction} items-center gap-8`}>
          
          {/* Website Preview with Browser Frame */}
          <div className="w-full lg:w-1/2 group/browser relative">
            <div className="bg-[#1a1a1a] rounded-t-lg p-2 flex gap-1.5 border-x border-t border-white/10">
              <div className="w-2 h-2 rounded-full bg-red-500/50" />
              <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
              <div className="w-2 h-2 rounded-full bg-green-500/50" />
            </div>
            <div className="aspect-video rounded-b-xl overflow-hidden border border-white/10 relative">
              <iframe
                src={url}
                title={title}
                className="w-[400%] h-[400%] border-none origin-top-left scale-[0.25] pointer-events-none transition-transform duration-700 group-hover/browser:scale-[0.28]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/browser:opacity-100 transition-all flex items-center justify-center backdrop-blur-[2px]">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-black px-6 py-2.5 rounded-full font-bold text-sm flex items-center gap-2 hover:scale-105 transition-transform"
                >
                  LIVE PREVIEW <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="flex-1 space-y-4">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span key={tag} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded bg-[#00F5A0]/10 text-[#00F5A0] border border-[#00F5A0]/20">
                  {tag}
                </span>
              ))}
            </div>
            
            <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
              {title}
            </h3>

            <p className="text-white/50 text-sm leading-relaxed leading-7">
              {desc}
            </p>

            <div className="pt-4 border-t border-white/5 flex items-center gap-6">
               <div className="flex items-center gap-2 text-[11px] text-white/40 font-mono">
                  <Layers size={14} className="text-[#00F5A0]" /> REACT / TAILWIND
               </div>
               <div className="flex items-center gap-2 text-[11px] text-white/40 font-mono">
                  <Globe size={14} className="text-[#00CFFF]" /> RESPONSIVE
               </div>
            </div>
          </div>

        </div>
      </div>
    </motion.div>
  );
}

export default function WebDevelopmentUI() {
  const [showAll, setShowAll] = useState(false);

  const allWebsites = [
    { id: 1, url: "https://construction-liard-two.vercel.app/", title: "Construction Portal", desc: "A heavy-duty corporate solution for builders. Built with custom animations and a service-first layout.", tags: ["Real Estate", "Corporate"] },
    { id: 2, url: "https://zayan-seven.vercel.app/", title: "Zayan Portfolio", desc: "Sleek, dark-themed personal brand showcase focusing on high-end typography and smooth scrolling.", tags: ["Branding", "Creative"] },
    { id: 3, url: "https://zak-cwo2.vercel.app/", title: "Zak Agency", desc: "A high-conversion landing page for digital agencies featuring interactive service grids.", tags: ["Agency", "Marketing"] },
    { id: 4, url: "https://zakmrk.netlify.app/", title: "Zak Mark", desc: "Clean marketing interface optimized for lead generation and brand storytelling.", tags: ["Startup", "UI/UX"] },
    { id: 5, url: "https://bonsai-tree-sigma.vercel.app/", title: "Bonsai Tree", desc: "Minimalist e-commerce concept for nature lovers. Focus on product photography and white space.", tags: ["E-Commerce", "Nature"] },
    { id: 6, url: "https://laals-chocolate.vercel.app/", title: "Laals Chocolate", desc: "Luxury confectionery site with rich colors and elegant product layouts.", tags: ["Food & Beverage", "Luxury"] },
    { id: 7, url: "https://da-cake.vercel.app/same-day", title: "Da Cake Shop", desc: "Dynamic bakery shop with filtering and same-day delivery focus.", tags: ["Local Business", "Ordering"] },
    { id: 8, url: "https://ventura-fawn-gamma.vercel.app/", title: "Ventura Gamma", desc: "A bold business landing page with geometric design elements.", tags: ["B2B", "Professional"] },
  ];

  const visibleSites = showAll ? allWebsites : allWebsites.slice(0, 3);

  const gradientText = {
    background: "linear-gradient(90deg, #00F5A0, #00CFFF)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  return (
    <section className=" py-24 px-6 lg:px-20 text-white relative overflow-hidden">
      
      {/* Mobile Heading */}
      <h2
        className="text-4xl font-extrabold mb-10 text-center xl:hidden"
        style={gradientText}
      >
        WEB DEVELOPMENT
      </h2>

      {/* Sidebar Heading */}
      <h2
        className="hidden xl:block absolute top-1/2 left-0 -translate-y-1/2 text-6xl font-extrabold tracking-wide select-none"
        style={{
          ...gradientText,
          transform: "rotate(-90deg) translateY(50%)",
          transformOrigin: "left center",
        }}
      >
        WEB DEVELOPMENT
      </h2>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-6">
          <div className="space-y-4">
            <h2 className="text-sm font-mono tracking-[0.3em] text-[#00F5A0] uppercase">Selected Works</h2>
            <h1 className="text-5xl md:text-7xl font-black uppercase">
              Web <span className="text-white/20">Projects</span>
            </h1>
          </div>
          <p className="max-w-xs text-white/40 text-sm italic border-l border-[#00F5A0] pl-4">
            Building digital experiences that combine aesthetic beauty with technical excellence.
          </p>
        </div>

        {/* Dynamic Project Grid */}
        <div className="grid grid-cols-1 gap-12">
          {visibleSites.map((site, index) => (
            <ContentBlock
              key={site.id}
              {...site}
              count={(index + 1).toString().padStart(2, '0')}
              reverse={index % 2 !== 0}
            />
          ))}
        </div>

        {/* Load More Button */}
        <div className="mt-20 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white/5 border border-white/10 rounded-full font-bold hover:bg-white hover:text-black transition-all duration-300"
          >
            {showAll ? "VIEW LESS PROJECTS" : "EXPLORE ALL WORKS"}
            <span className="bg-[#00F5A0] w-2 h-2 rounded-full group-hover:bg-black" />
          </button>
        </div>
      </div>
    </section>
  );
}